import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';
import { change, getFormValues, touch } from 'redux-form';

import AmenityReservationFormComponent from './AmenityReservationFormComponent';

import {
  getBuilding,
  getBuildingResidents,
  getBuildingsForFilter,
} from '../Building/actionCreators';
import {
  clearAmenities,
  getAllowedHours,
  getAmenities,
  getAmenity,
  getAmenityReservation,
  getReservedDates,
  reserveAmenity,
  updateAmenityReservation,
} from './actions';

import { getFullName } from '../Person/utils';
import {
  convertDataToMinutes,
  getAmenityFilterOptions,
  getBuildingFilterOptions,
  getBuildingTimezone,
  getDisabledHours,
  getDisabledMinutes,
  getMomentDateAndTime,
  getPersonFilterOptions,
  getReservationInitialValues,
  isInvalidDate,
  minsToHHMMSS,
  onDateChangeFormatter,
} from './utils';

import {
  DEFAULT_END_ANY_TIME,
  DEFAULT_START_TIME,
} from '../Schedule/constants';

const form = 'AmenityReservation';

const AmenityReservationFormContainer = (props) => {
  const {
    selectedBuildingId,
    getBuilding,
    amenityId,
    getAmenities,
    getContactPersons,
    getAmenity,
    reserveAmenity,
    updateAmenityReservation,
    clearAmenities,
    onFieldChange,
    formValues,
    startDate,
    isCreate,
    getAllowedHours,
    selectedAmenity,
    startTime: initialStartTime,
    endTime: initialEndTime,
  } = props;

  const [isPreview, setIsPreview] = useState(false);
  const [reservationDate, setReservationDate] = useState();
  const [selectedStartTime, setSelectedStartTime] = useState();
  const [selectedDuration, setSelectedDuration] = useState();

  useEffect(() => {
    // TODO: check if that condition and state needed
    if (!isCreate) {
      setIsPreview(false); // we don`t need inputs to be disabled anymore
    }
    return () => clearAmenities();
  }, []);

  useEffect(() => {
    if (!selectedBuildingId) return;
    isCreate && getBuilding(selectedBuildingId);
    getAmenities(selectedBuildingId);
    getContactPersons(selectedBuildingId);
  }, [selectedBuildingId]);

  useEffect(() => {
    if (!selectedBuildingId || !amenityId) return;
    getAmenity(amenityId);
    isCreate && onFieldChange('startDate', null);
    isCreate && onFieldChange('endDate', null);
    setReservationDate();
  }, [amenityId]);

  const [selectedDate] = getMomentDateAndTime(startDate);
  useEffect(() => {
    if (!selectedBuildingId || !amenityId || !startDate) return;
    getAllowedHours(amenityId, selectedDate);
    setReservationDate(selectedDate);
  }, [selectedDate, reservationDate]);

  const shouldDisableDate = (day) => isInvalidDate(day, selectedAmenity);

  const onGetDisabledHours = (fieldName) =>
    getDisabledHours(fieldName, startDate, selectedAmenity);

  const onGetDisabledMinutes = (selectedHour, fieldName) =>
    getDisabledMinutes(selectedHour, fieldName, startDate, selectedAmenity);

  // now it should be possible to select only one day ->
  // startDate should === endDate (but not time)
  const onDayChange = (event, newValue, previousValue, name) => {
    const field = name === 'startDate' ? 'endDate' : 'startDate';
    const prevFieldValue = formValues?.[field];
    const value = onDateChangeFormatter(newValue, prevFieldValue);
    return onFieldChange(field, value);
  };

  const onSubmit = (data) => {
    const { building, startDate, endDate, ...rest } = data;
    let startTime = null;
    let endTime = null;

    if (isCreate) {
      startTime = minsToHHMMSS(selectedStartTime);
      endTime = minsToHHMMSS(selectedStartTime + selectedDuration);
    } else {
      if (selectedStartTime) {
        startTime = minsToHHMMSS(selectedStartTime);
      }
      if (!selectedStartTime) {
        startTime = initialStartTime;
      }
      if (selectedDuration) {
        if (selectedStartTime) {
          endTime = minsToHHMMSS(selectedStartTime + selectedDuration);
        } else {
          const res = convertDataToMinutes(initialStartTime) + selectedDuration;
          endTime = minsToHHMMSS(res);
        }
      }
      if (!selectedDuration) {
        if (selectedStartTime) {
          // here we need to get duration and add it to new start time
          const duration =
            convertDataToMinutes(initialEndTime) -
            convertDataToMinutes(initialStartTime);
          endTime = minsToHHMMSS(selectedStartTime + duration);
        } else {
          endTime = initialEndTime;
        }
      }
    }

    const [date] = getMomentDateAndTime(startDate);
    // const [, endTime] = getMomentDateAndTime(endDate);
    const { id, comments, contactPerson } = rest;
    const values = {
      date,
      startTime,
      endTime,
      id,
      comments,
      contactPerson,
    };

    // check if a user has selected 12:00 am of the next day ->
    // replace with 23:59 pm (due to the one day limit and 15-min intervals)
    if (values?.endTime === DEFAULT_START_TIME) {
      values.endTime = DEFAULT_END_ANY_TIME;
    }
    return isCreate ? reserveAmenity(values) : updateAmenityReservation(values);
  };

  return (
    <AmenityReservationFormComponent
      {...props}
      isPreview={isPreview}
      setIsPreview={setIsPreview}
      onSubmit={onSubmit}
      onDayChange={onDayChange}
      shouldDisableDate={shouldDisableDate}
      getDisabledHours={onGetDisabledHours}
      getDisabledMinutes={onGetDisabledMinutes}
      reservationDate={reservationDate}
      setReservationDate={setReservationDate}
      setSelectedStartTime={setSelectedStartTime}
      setSelectedDuration={setSelectedDuration}
      selectedStartTime={selectedStartTime}
      selectedDuration={selectedDuration}
    />
  );
};

AmenityReservationFormContainer.defaultProps = {
  selectedBuildingId: null,
  amenityId: null,
  formValues: {},
  startDate: null,
  selectedAmenity: null,
};

AmenityReservationFormContainer.propTypes = {
  selectedBuildingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  getBuilding: PropTypes.func.isRequired,
  amenityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  getAmenities: PropTypes.func.isRequired,
  getContactPersons: PropTypes.func.isRequired,
  getAmenity: PropTypes.func.isRequired,
  reserveAmenity: PropTypes.func.isRequired,
  updateAmenityReservation: PropTypes.func.isRequired,
  clearAmenities: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  formValues: PropTypes.instanceOf(Object),
  startDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Object),
  ]),
  isCreate: PropTypes.bool.isRequired,
  getAllowedHours: PropTypes.func.isRequired,
  selectedAmenity: PropTypes.instanceOf(Object),
  startTime: PropTypes.string,
  endTime: PropTypes.string,
};

const mapStateToProps = (state, ownProps) => {
  const { buildings, amenity, people, common } = state;
  const pmcId = ownProps?.match?.params?.PMCId;
  const buildingId = ownProps?.match?.params?.buildingId;
  const reservationId = ownProps?.match?.params?.id;
  const isCreate = reservationId === 'create';

  const startTime = amenity?.amenityReservation?.startTime;
  const endTime = amenity?.amenityReservation?.endTime;

  const reservation = amenity?.amenityReservation ?? {};
  const initialValues = isCreate
    ? {
        building: buildingId,
        startTime: null,
        endTime: null,
      }
    : getReservationInitialValues(reservation);
  const buildingOptions = getBuildingFilterOptions(
    buildings,
    pmcId,
    buildingId,
  );
  const isExpired = !isCreate && reservation?.expired;

  if (isCreate && !buildingId && buildingOptions?.length === 1) {
    initialValues.building = buildingOptions?.[0]?.value;
  }

  const amenityOptions = amenity?.amenities
    ? getAmenityFilterOptions(amenity?.amenities)
    : [];

  const formValues = getFormValues(form)(state);
  const selectedBuildingId = formValues?.building;
  const amenityId = formValues?.id;
  const startDate = formValues?.startDate;
  const endDate = formValues?.endDate;

  const selectedAmenity = amenityId && amenity?.details?.[amenityId];
  const selectedBuildingSettings = buildings?.[selectedBuildingId]?.settings;

  const timezoneName =
    selectedAmenity?.data?.timezone ??
    getBuildingTimezone(common, buildings, selectedBuildingId);

  const personOptions = getPersonFilterOptions(Object.values(people));

  const contactPersonId = reservation?.contactPerson?.id;
  if (
    contactPersonId &&
    !personOptions?.find(({ value }) => value === contactPersonId)
  ) {
    personOptions.push({
      label: getFullName(reservation?.contactPerson),
      value: contactPersonId,
    });
  }

  const isOutOfAllowedDateRange =
    !isCreate && isInvalidDate(initialValues?.startDate, selectedAmenity);

  return {
    form,
    isCreate,
    isExpired,
    initialValues,
    formValues,
    buildingOptions,
    amenityOptions,
    personOptions,
    selectedBuildingId,
    selectedBuildingSettings,
    amenityId,
    selectedAmenity,
    startDate,
    endDate,
    startTime,
    endTime,
    isOutOfAllowedDateRange,
    timezoneName,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const reservationId = ownProps?.match?.params?.id;
  const isCreate = reservationId === 'create';
  return {
    onLoad: () => {
      getBuildingsForFilter(dispatch);
      !isCreate && getAmenityReservation(dispatch, reservationId);
    },
    getBuilding: (buildingId) => getBuilding(dispatch, buildingId),
    reserveAmenity: (values) => reserveAmenity(dispatch, values?.id, values),
    updateAmenityReservation: (data) =>
      updateAmenityReservation(dispatch, reservationId, data),
    getAmenities: (building) => {
      dispatch(touch(form, 'comments'));
      getAmenities(dispatch, { building, disable_pagination: true });
    },
    getContactPersons: (building) =>
      getBuildingResidents(dispatch, { disable_pagination: true }, building),
    getAmenity: (amenityId) => {
      getAmenity(dispatch, amenityId);
      getReservedDates(dispatch, amenityId);
    },
    getAllowedHours: (amenityId, date) =>
      getAllowedHours(dispatch, amenityId, date, !isCreate && reservationId),
    clearAmenities: () => clearAmenities(dispatch),
    onFieldChange: (field, value) => dispatch(change(form, field, value)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AmenityReservationFormContainer);
